import {
  DialogContent,
  Dialog,
  DialogProps,
  DialogActions,
  Grid,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import { PmiDialogTitle } from 'shell/components/shared/dialog/title';
import { styled } from '@mui/styles';
import { useFormik } from 'formik';
import PmiTextField from 'shell/components/shared/text-field';
import * as yup from 'yup';
import { DateTime } from 'luxon';
import MunicipalityList, { MunicipalityAnprVM } from 'shell/components/shared/municipality-list';
import { DatePicker } from '@mui/lab';
import { FileExtended } from 'shell/view-models/edit-data-control-person';

export const notificationValidationSchema = yup.object({
  address: yup.string().nullable().required('Il campo è obbligatorio'),
  numberCivic: yup.string().nullable().required('Il campo è obbligatorio'),
  letterCivic: yup.string().nullable().required('Il campo è obbligatorio'),
});

export const PmiDialog = styled((props: DialogProps) => <Dialog {...props} />)((props) => ({
  '& .MuiDialog-paper': props.fullScreen
    ? {
        minWidth: 'unset',
        maxWidth: '100%',
      }
    : {
        minWidth: '50rem',
        maxWidth: '800px',
      },
}));

type NotificationState = {
  id: string;
  state: string;
  date: DateTime | null;
  communicationType: string;
  surnameNameReceiver: string;
  pecAddress: string;
  address: string;
  cap: string;
  municipality?: MunicipalityAnprVM | null;
  location: string;
  files?: Array<FileExtended>;
};

interface ModalNotificationStateProps {
  title?: string;
  modalData: NotificationState;
  open: boolean;
  onClose: (open: boolean) => void;
  onSubmit: (values: NotificationState) => void;
  isEdit?: boolean;
}

const ModalNotificationState = ({
  title,
  modalData,
  open,
  onClose,
  onSubmit,
  isEdit,
}: ModalNotificationStateProps): React.ReactElement => {
  const form = useFormik<NotificationState>({
    initialValues: modalData,
    validationSchema: notificationValidationSchema,
    onSubmit,
  });

  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down(theme.breakpoints.values.md));

  return (
    <PmiDialog
      open={open}
      onClose={() => onClose(false)}
      aria-labelledby="alert-dialog-title"
      aria-describedby="alert-dialog-description"
      fullScreen={fullScreen}
    >
      <PmiDialogTitle id="alert-dialog-title" backgroundColor="primary" color="white">
        {title}
      </PmiDialogTitle>
      <DialogContent style={{ padding: '30px 24px' }}>
        <Grid container spacing={1}>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="idetinfier"
              label="ID"
              aria-label="ID"
              fullWidth
              disabled
              helperText={form.errors.id}
              error={form.touched.id && Boolean(form.errors.id)}
              value={form.values.id || ''}
              onChange={(event) => form.setFieldValue('id', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="state"
              label="Stato"
              aria-label="Stato"
              fullWidth
              disabled
              helperText={form.errors.state}
              error={form.touched.id && Boolean(form.errors.state)}
              value={form.values.state || ''}
              onChange={(event) => form.setFieldValue('state', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <DatePicker
              label="Data"
              aria-label="Data"
              value={form.values?.date || null}
              disabled
              onChange={(date) => form.setFieldValue('date', date)}
              renderInput={(params) => (
                <PmiTextField
                  {...params}
                  id="date"
                  fullWidth
                  error={form.touched?.date && Boolean(form.errors?.date)}
                  helperText={form.errors?.date}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="communicationType"
              label="Tipo Comunicazione"
              aria-label="Tipo Comunicazione"
              fullWidth
              disabled
              helperText={form.errors.communicationType}
              error={form.touched.communicationType && Boolean(form.errors.communicationType)}
              value={form.values.communicationType || ''}
              onChange={(event) => form.setFieldValue('communicationType', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={9}>
            <PmiTextField
              id="surnameNameReceiver"
              label="Cognome Nome destinatario"
              aria-label="Cognome Nome destinatario"
              fullWidth
              disabled={!isEdit}
              helperText={form.errors.surnameNameReceiver}
              error={form.touched.surnameNameReceiver && Boolean(form.errors.surnameNameReceiver)}
              value={form.values.surnameNameReceiver || ''}
              onChange={(event) => form.setFieldValue('surnameNameReceiver', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <PmiTextField
              id="pecAddress"
              label="PEC"
              aria-label="PEC"
              fullWidth
              disabled={!isEdit}
              helperText={form.errors.pecAddress}
              error={form.touched.pecAddress && Boolean(form.errors.pecAddress)}
              value={form.values.pecAddress || ''}
              onChange={(event) => form.setFieldValue('pecAddress', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <PmiTextField
              id="address"
              label="Indirizzo"
              aria-label="Indirizzo"
              fullWidth
              disabled={!isEdit}
              helperText={form.errors.address}
              error={form.touched.address && Boolean(form.errors.address)}
              value={form.values.address || ''}
              onChange={(event) => form.setFieldValue('address', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="cap"
              label="CAP"
              aria-label="CAP"
              fullWidth
              disabled={!isEdit}
              helperText={form.errors.cap}
              error={form.touched.cap && Boolean(form.errors.cap)}
              value={form.values.cap || ''}
              onChange={(event) => form.setFieldValue('cap', event.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <MunicipalityList
              id="municipality"
              fullWidth
              helperText={form.errors.municipality}
              error={form.touched.municipality && Boolean(form.errors.municipality)}
              value={form.values.municipality || null}
              disabled={!isEdit}
              onChange={(value) => form.setFieldValue('municipality', value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="province"
              label="Provincia"
              aria-label="Provincia"
              fullWidth
              disabled
              value={form.values.municipality?.province || ''}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <PmiTextField
              id="location"
              label="Località"
              aria-label="Località"
              fullWidth
              disabled={!isEdit}
              onChange={(event) => form.setFieldValue('location', event.target.value)}
              value={form.values.location || ''}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {/* <PmiButton
          variant="contained"
          color="primary"
          type="submit"
          disabled={!isEdit}
          onClick={() => {
            form.handleSubmit();
          }}
        >
          Invia notifica
        </PmiButton> */}
        <PmiButton variant="outlined" color="cdmBrand" type="button" onClick={() => onClose(false)}>
          Chiudi
        </PmiButton>
      </DialogActions>
    </PmiDialog>
  );
};

ModalNotificationState.displayName = 'Modal notification State';

export default ModalNotificationState;
